import { GroupProductList } from '@shared/api/types';
import Text from '@shared/components/content/text';
import Unit from '@shared/components/content/unit';
import { DateCell } from '@shared/components/data-table/cells/date-cell';
import { HeaderCell } from '@shared/components/data-table/cells/header-cell';
import { NumberCell } from '@shared/components/data-table/cells/number-cell';
import RatingCloud, {
  RatingCloudProps,
} from '@shared/components/ratings/rating-cloud';
import { ColumnDef } from '@tanstack/react-table';

export function useGroupProductsColumns(): ColumnDef<GroupProductList>[] {
  return [
    {
      id: 'uuid',
      accessorKey: 'uuid',
      enableHiding: true,
    },
    {
      id: 'name',
      accessorKey: 'name',
      header: ({ column }) => <HeaderCell column={column} name="Product" />,
      cell: ({ getValue }) => {
        const name = getValue<string>();
        return (
          <Text
            includeHover
            className="line-clamp-2 truncate max-w-[240px]"
            variant="subtle"
          >
            {name}
          </Text>
        );
      },
      enableColumnFilter: true,
      meta: {
        label: 'Product',
        variant: 'text',
        placeholder: 'Search products…',
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
    {
      id: 'organizationName',
      accessorKey: 'organizationName',
      header: ({ column }) => (
        <HeaderCell column={column} name="Organisation" />
      ),
      cell: ({ getValue }) => (
        <Text variant="subtle">{getValue<string>()}</Text>
      ),
    },
    {
      id: 'modifiedTime',
      accessorKey: 'modifiedTime',
      header: ({ column }) => (
        <HeaderCell column={column} name="Last updated" />
      ),
      cell: ({ getValue }) => <DateCell date={getValue<string>()} />,
    },
    {
      id: 'totalEmissions',
      accessorKey: 'totalEmissions',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Total emissions"
          numberColumn
          unit={<Unit variant="kgCO2e" />}
        />
      ),
      cell: ({ getValue }) => {
        const value = getValue<number | null>();
        if (value === null)
          return <p className="pr-[18px] text-right text-gray-500">-</p>;
        return <NumberCell number={value} />;
      },
    },
    {
      id: 'totalEmissionsPerKg',
      accessorKey: 'totalEmissionsPerKg',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Intensity"
          numberColumn
          unit={<Unit variant="kgCO2e/kg" />}
        />
      ),
      cell: ({ getValue }) => {
        const value = getValue<number | null>();
        if (value === null)
          return <p className="pr-[18px] text-right text-gray-500">-</p>;
        return <NumberCell number={value} />;
      },
    },
    {
      id: 'emissionsCategory',
      accessorKey: 'emissionsCategory',
      header: ({ column }) => <HeaderCell column={column} name="Rating" />,
      cell: ({ getValue }) => {
        const rating = getValue<RatingCloudProps['rating'] | null>();
        if (!rating) return null;
        return <RatingCloud rating={rating} />;
      },
      enableSorting: false,
    },
  ];
}
